(function () {
  let isAdding = false;
  let isSyncing = false;

  function asNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  async function getCart() {
    const response = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'));
    return response.json();
  }

  async function fetchCartMutation(url, body) {
    const response = await fetch(url, {
      ...theme.utils.fetchConfig('json'),
      body: JSON.stringify(body),
    });

    return response.json();
  }

  function publishCartUpdate(source, cart, sections) {
    cart.sections = sections;
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source,
      cart,
    });
  }

  function getConfig() {
    const element = document.querySelector('.olgc-milestone-rewards[data-mystery-gift-variant-id]');
    if (!element) return null;

    const mysteryGiftVariantId = asNumber(element.dataset.mysteryGiftVariantId);
    const mysteryGiftThreshold = asNumber(element.dataset.mysteryGiftThreshold);
    const premiumGiftVariantId = asNumber(element.dataset.premiumGiftVariantId);
    const premiumGiftThreshold = asNumber(element.dataset.premiumGiftThreshold);
    const giftWrapVariantId = asNumber(element.dataset.giftWrapVariantId);

    if (!mysteryGiftVariantId || !mysteryGiftThreshold) return null;

    return {
      mysteryGiftVariantId,
      mysteryGiftThreshold,
      premiumGiftVariantId,
      premiumGiftThreshold,
      giftWrapVariantId,
    };
  }

  function isRewardLine(item, config) {
    return hasRewardProperty(item) || item.variant_id === config.mysteryGiftVariantId || item.variant_id === config.premiumGiftVariantId;
  }

  function hasRewardProperty(item) {
    const properties = item.properties || {};

    if (Array.isArray(properties)) {
      return properties.some(([name, value]) => name === '_olgc_milestone_reward' && value);
    }

    return Boolean(properties._olgc_milestone_reward);
  }

  function getEligibleSubtotal(cart, config) {
    return cart.items.reduce((total, item) => {
      if (isRewardLine(item, config) || item.variant_id === config.giftWrapVariantId) return total;
      return total + item.final_line_price;
    }, 0);
  }

  function getEligibleRewardLabel(eligibleSubtotal, config) {
    if (config.premiumGiftVariantId && config.premiumGiftThreshold && eligibleSubtotal >= config.premiumGiftThreshold) {
      return 'Premium Gift';
    }

    if (eligibleSubtotal >= config.mysteryGiftThreshold) {
      return 'Mystery Gift';
    }

    return '';
  }

  async function removeUnqualifiedRewards() {
    if (isSyncing || !window.theme?.routes || !window.theme?.pubsub) return;

    const config = getConfig();
    if (!config) return;

    isSyncing = true;

    try {
      const cart = await getCart();
      const eligibleSubtotal = getEligibleSubtotal(cart, config);
      const updates = {};
      const attributes = {};
      const selectedReward = cart.attributes?.['Milestone Reward'] || '';
      const eligibleRewardLabel = getEligibleRewardLabel(eligibleSubtotal, config);

      for (const item of cart.items) {
        const hasLegacyRewardMarker = hasRewardProperty(item);
        const isMysteryGift = item.variant_id === config.mysteryGiftVariantId;
        const isPremiumGift = item.variant_id === config.premiumGiftVariantId;
        const isStaleReward = hasLegacyRewardMarker && !isMysteryGift && !isPremiumGift;
        const shouldRemoveMysteryGift = isMysteryGift && eligibleRewardLabel !== 'Mystery Gift';
        const shouldRemovePremiumGift = isPremiumGift && eligibleRewardLabel !== 'Premium Gift';

        if (isStaleReward || shouldRemoveMysteryGift || shouldRemovePremiumGift) {
          updates[item.key] = 0;
        }
      }

      if (selectedReward && selectedReward !== eligibleRewardLabel) {
        attributes['Milestone Reward'] = '';
      }

      if (Object.keys(updates).length === 0 && Object.keys(attributes).length === 0) return;

      const sections = getBundledSections();
      const parsedState = await fetchCartMutation(theme.routes.cart_update_url, {
        updates,
        attributes,
        sections,
        sections_url: window.location.pathname,
      });

      if (!parsedState.status) {
        publishCartUpdate('olgc-milestone-rewards-cleanup', parsedState, parsedState.sections);
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      isSyncing = false;
    }
  }

  async function addReward(event) {
    const button = event.target.closest('[data-olgc-add-reward]');
    if (!button || isAdding || !window.theme?.routes || !window.theme?.pubsub) return;

    const variantId = asNumber(button.dataset.rewardVariantId);
    if (!variantId) return;

    isAdding = true;
    button.disabled = true;
    button.classList.add('is-adding');

    try {
      const sections = getBundledSections();
      const cart = await getCart();
      const config = getConfig();
      const updates = {};

      if (config) {
        for (const item of cart.items) {
          if (isRewardLine(item, config)) {
            updates[item.key] = 0;
          }
        }
      }

      const parsedState = await fetchCartMutation(theme.routes.cart_update_url, {
        updates,
        attributes: {
          'Milestone Reward': button.dataset.rewardLabel || 'Free Gift',
        },
        sections_url: window.location.pathname,
      });

      if (parsedState.status) {
        button.disabled = false;
        button.classList.remove('is-adding');
        return;
      }

      const addState = await fetchCartMutation(theme.routes.cart_add_url, {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _olgc_milestone_reward: button.dataset.rewardLabel || 'Free Gift',
            },
          },
        ],
        sections,
        sections_url: window.location.pathname,
      });

      if (addState.status) {
        button.disabled = false;
        button.classList.remove('is-adding');
        return;
      }

      const cartJson = await getCart();
      publishCartUpdate('olgc-milestone-rewards', cartJson, addState.sections);
    }
    catch (error) {
      button.disabled = false;
      button.classList.remove('is-adding');
      console.error(error);
    }
    finally {
      isAdding = false;
    }
  }

  function scheduleRewardCleanup() {
    window.requestAnimationFrame(removeUnqualifiedRewards);
  }

  document.addEventListener('click', addReward);
  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === 'olgc-milestone-rewards-cleanup') return;
    scheduleRewardCleanup();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRewardCleanup);
  }
  else {
    scheduleRewardCleanup();
  }
})();
